import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

/**
 * Row-aware spatial cell accessor (Global Constraints: row-aware access rule).
 *
 * EVERY surface (cell renderer, peek, single-row, copy/export transforms,
 * layer data) resolves a column's geometry input through
 * `resolveSpatialCellInput` — nobody reads the cell value directly, because
 * declared lat-lng pairs (T18) need the sibling column's value.
 *
 * The declaration types are hoisted HERE (A5/CD4+G8): ten downstream tasks
 * compile against these shapes from T4 onward. T18 only POPULATES the declared
 * arm — its persisted `electron/metaInfo/types.ts` version is the authoritative
 * SUPERSET (adds `createdAt`); keep the structural fields aligned. Renderer
 * code uses THIS type; the accessor's declared arm (T18) builds it field-by-
 * field from the MetaInfo record — it never imports the electron type directly.
 */
export type SpatialColumnDeclarationFormat = 'wkt' | 'geojson' | 'latlng-text' | 'lat-lng-pair';

export type SpatialColumnDeclaration = {
  id: string;
  schema: string;
  table: string;
  /** For `lat-lng-pair` this is the LAT column; `lngColumn` names the LNG column. */
  column: string;
  format: SpatialColumnDeclarationFormat;
  /** `lat-lng-pair` only. */
  lngColumn?: string;
  /** `latlng-text` only; defaults to `lat-lng`. */
  axisOrder?: 'lat-lng' | 'lng-lat';
};

/**
 * Geometry input for a single cell. `declaration` is present from day one
 * (undefined on the native arm; the declared arm tags it in T18).
 */
export type SpatialCellInput =
  | { engine: DatabaseEngineId; value: unknown; dataType?: string; declaration?: SpatialColumnDeclaration }
  | null;

/**
 * Discriminated spatial payload threaded through `openExpandedTextDialog` (T11)
 * so the expanded-dialog map pane can decode the ORIGINAL wire value. Carries
 * the raw `SpatialCellInput` (a hex/WKT string, an Oracle/MSSQL marker OBJECT,
 * or a declared lat/lng pair) — NEVER a pre-stringified value (A13/G16: that
 * would destroy SDO/MSSQL markers, declared pairs, and SRID). `fetchFullValue`
 * is present only for windowed grids whose cell is a truncated preview.
 */
export type ExpandedSpatialPayload = {
  input: SpatialCellInput;
  engine: DatabaseEngineId;
  dataType?: string;
  fetchFullValue?: () => Promise<unknown>;
  /** Resolve non-bundled SRIDs from this connection's `spatial_ref_sys` (T12 ladder). */
  connectionId?: string;
  database?: string;
};

export type SpatialAccessorContext = {
  engine: DatabaseEngineId;
  nativeSpatialColumnIds: Set<string>;
  /** columnId → dataType. */
  columnDataTypes: Map<string, string>;
  /** columnId → declaration. **T18** fills this; undefined/empty on the native arm. */
  declaredSpatialColumns?: Map<string, SpatialColumnDeclaration>;
};

/**
 * Resolve the geometry input for `columnId` in `rowData`.
 *
 * **T4 implements the native arm only:** when `columnId ∈ nativeSpatialColumnIds`
 * the input is `{ engine, value: rowData[columnId], dataType }` with no
 * declaration. **T18** adds the declared arm: for `lat-lng-pair` the returned
 * input carries `value: { lat, lng }` assembled from BOTH columns AND the
 * declaration; for wkt/geojson/latlng-text it carries the cell value + the
 * declaration.
 */
/**
 * Eager-safe one-line summary for a DECLARED spatial cell (T18). The grid cell can't run the wkx
 * sniffer on a declared value (a lat/lng pair is a plain number; a WKT/GeoJSON string isn't a wire
 * format), so this formats a cheap label from the resolved value WITHOUT importing the decoder core
 * (bundle isolation). Returns null for an empty pair so the cell falls through to the NULL render.
 */
export function formatDeclaredSpatialSummary(
  declaration: SpatialColumnDeclaration,
  value: unknown,
): string | null {
  if (declaration.format === 'lat-lng-pair') {
    const pair = value as { lat?: unknown; lng?: unknown } | null;
    if (!pair || (pair.lat == null && pair.lng == null)) return null;
    return `POINT · ${String(pair.lat ?? '—')}, ${String(pair.lng ?? '—')}`;
  }
  if (declaration.format === 'geojson') {
    let type: string | undefined;
    if (value && typeof value === 'object') {
      type = (value as { type?: unknown }).type as string | undefined;
    } else if (typeof value === 'string') {
      try {
        type = (JSON.parse(value) as { type?: unknown }).type as string | undefined;
      } catch {
        type = undefined;
      }
    }
    return type ? `${type} · GeoJSON` : 'GeoJSON';
  }
  // wkt / latlng-text: the raw text is already human-readable — show it (truncated by the cell CSS).
  const text = value == null ? '' : String(value);
  return text.length > 0 ? text : null;
}

export function resolveSpatialCellInput(
  columnId: string,
  rowData: Record<string, unknown> | undefined,
  ctx: SpatialAccessorContext,
): SpatialCellInput {
  if (!rowData) return null;
  if (ctx.nativeSpatialColumnIds.has(columnId)) {
    return {
      engine: ctx.engine,
      value: rowData[columnId],
      dataType: ctx.columnDataTypes.get(columnId),
    };
  }
  // T18 declared arm. The map is keyed by the grid's columnId (id === key ===
  // source column name in both grids), so `rowData[columnId]` is this cell's
  // value and `rowData[lngColumn]` reads the sibling LNG cell for a pair.
  const declaration = ctx.declaredSpatialColumns?.get(columnId);
  if (declaration) {
    if (declaration.format === 'lat-lng-pair') {
      // Row-aware assembly: the logical map value is built from BOTH columns.
      const lat = rowData[columnId];
      const lng =
        declaration.lngColumn != null ? rowData[declaration.lngColumn] : undefined;
      return {
        engine: ctx.engine,
        value: { lat, lng },
        dataType: ctx.columnDataTypes.get(columnId),
        declaration,
      };
    }
    // wkt / geojson / latlng-text: tag the cell's own value with the declaration.
    return {
      engine: ctx.engine,
      value: rowData[columnId],
      dataType: ctx.columnDataTypes.get(columnId),
      declaration,
    };
  }
  return null;
}
